import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { queryClient } from './lib/queryClient';
import { AuthProvider } from './auth/AuthContext';
import { OrgProvider } from './org/OrgContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { fetchAccounts } from './api';
import { currentAppPath, getSlot, routerBasename } from './lib/accountSlot';
import { bareLinkTarget } from './lib/accountRouting';
import './index.css';

// Defense-in-depth for deploys: a tab left open across a deploy may try to load
// a code-split chunk whose content-hashed filename no longer exists on the
// server (the old dist is fully replaced). Vite emits `vite:preloadError` for
// that. Reload to pull the fresh, `no-cache` index.html and its current asset
// hashes. The timestamp guard re-arms after 10s so a later deploy in a
// long-lived session still recovers, but a genuinely unavailable asset can't
// cause a reload loop.
window.addEventListener('vite:preloadError', () => {
  const KEY = 'aiai:last-chunk-reload';
  const now = Date.now();
  const last = Number(sessionStorage.getItem(KEY) ?? 0);
  if (now - last < 10_000) return;
  sessionStorage.setItem(KEY, String(now));
  window.location.reload();
});

// Installable PWA: the service worker (src/sw/sw.ts, emitted as /sw.js) is
// production-only so it never caches Vite dev-server modules. Registration
// failure (e.g. plain-HTTP origins, where it isn't allowed) is harmless.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => undefined);
  });
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found');
}

// Nothing renders until the bare-link lookup settles, so a hung API must not
// blank the page.
const BARE_LINK_TIMEOUT_MS = 3000;

/**
 * A bare `/w/<slug>/...` link (Telegram notifications, old bookmarks) opens
 * under the first signed-in account that's a member of that workspace.
 * Resolved before the first render so the wrong account never flashes.
 */
async function resolveBareWorkspaceLink(): Promise<boolean> {
  const { pathname } = window.location;
  if (getSlot() !== 0 || !pathname.startsWith('/w/')) return false;
  const slug = pathname.split('/')[2];
  if (!slug) return false;
  try {
    const accounts = await fetchAccounts(slug, { timeoutMs: BARE_LINK_TIMEOUT_MS });
    const target = bareLinkTarget(currentAppPath(), accounts);
    if (target) {
      window.location.replace(target);
      return true;
    }
  } catch {
    // Unreachable or hung API: just boot on slot 0.
  }
  return false;
}

function render(root: HTMLElement): void {
  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <ErrorBoundary fullscreen>
        <QueryClientProvider client={queryClient}>
          {/* Each tab is one account: /u/N is the router's basename, so every
              route and link stays root-relative and picks it up. */}
          <BrowserRouter basename={routerBasename(getSlot())}>
            <AuthProvider>
              <OrgProvider>
                <App />
              </OrgProvider>
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </ErrorBoundary>
    </React.StrictMode>,
  );
}

void resolveBareWorkspaceLink().then((redirected) => {
  if (!redirected) render(rootElement);
});
